import { FC, ReactNode, useMemo } from 'react';
import {
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_TableOptions,
  MRT_TableInstance,
  MRT_TableBody as MRTTableBody,
  MRT_TableHead as MRTTableHead,
} from 'material-react-table';

import { TableCell, TableContainer, TableFooter, TableRow } from '@mui/material';
import { useInvoiceSummaryFromParams } from 'src/use-cases/invoice/useInvoices';
import { useGetInvoiceIssueSummary } from 'src/use-cases/invoice/useDispute';
import { formatMoney } from 'src/utils/utility';
import { IInvoiceIssueData } from './types';
import { TableMain, TableWrapper } from '../../../molecules/table/Table';

interface DisputeTableProps {
  table: MRT_TableInstance<IInvoiceIssueData>;
  footer?: ReactNode;
}

const invoiceIssueCategories = {
  accessorialMissingApprovals: 'Accessorial charges missing approvals',
  serviceTypeMissingApprovals: 'Shipments missing approvals for special service',
  deviations: 'Total number of charge deviations',
  rejectedNRCFShipments: 'Shipments rejected (no rate card)',
};

export const DisputeTable: FC<DisputeTableProps> = ({ table, footer }) => (
  <TableContainer component={TableWrapper}>
    <TableMain>
      <MRTTableHead table={table} />
      <MRTTableBody table={table} />
      {footer && <TableFooter>{footer}</TableFooter>}
    </TableMain>
  </TableContainer>
);

const useTableIssues = (items: IInvoiceIssueData[], tableOptions: Omit<MRT_TableOptions<IInvoiceIssueData>, 'data'>) =>
  useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableRowSelection: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: true,
    editDisplayMode: 'table',
    data: items || [],
    ...tableOptions,
  });

const useTableIssuesColumns = (): MRT_ColumnDef<IInvoiceIssueData>[] => [
  {
    enableEditing: false,
    accessorKey: 'no',
    header: 'No.',
    size: 20,
  },
  {
    enableEditing: false,
    accessorKey: 'category',
    header: 'Category',
    size: 180,
  },
  {
    enableEditing: false,
    accessorKey: 'quantity',
    header: 'Quantity',
    size: 80,
  },
  {
    enableEditing: false,
    accessorKey: 'shipmentsImpacted',
    header: 'Shipments Impacted',
    size: 240,
  },
  {
    enableEditing: false,
    accessorKey: 'totalAmount',
    header: 'Total Amount',
    size: 180,
    accessorFn(originalRow) {
      return <span className='text-red-40'>{formatMoney(originalRow.totalAmount, originalRow.currency)}</span>;
    },
  },
];

export const DisputeTableIssues = () => {
  const { data: invoiceSummary } = useInvoiceSummaryFromParams();
  const { data: invoiceIssues } = useGetInvoiceIssueSummary(invoiceSummary?.id);

  const issueItems: IInvoiceIssueData[] = useMemo(() => {
    const items = [];
    if (invoiceIssues) {
      let no = 1;
      for (const key in invoiceIssues) {
        if (typeof invoiceIssueCategories[key] !== 'undefined' && invoiceIssues[key]?.quantity > 0) {
          items.push({
            no,
            key,
            category: invoiceIssueCategories[key],
            quantity: invoiceIssues[key]?.quantity,
            shipmentsImpacted: invoiceIssues[key]?.shipmentImpacted?.join(', '),
            totalAmount: invoiceIssues[key]?.totalAmount?.afterTax,
            currency: invoiceIssues[key]?.totalAmount?.currency,
          });

          no += 1;
        }
      }
    }
    return items;
  }, [invoiceIssues]);

  const totalAmount: number = useMemo(() => {
    if (issueItems?.length > 0) {
      return issueItems.reduce((n, { totalAmount }) => n + totalAmount, 0);
    }
    return 0;
  }, [issueItems]);

  const totalCurrency: string = useMemo(() => {
    if (issueItems?.length > 0) {
      return issueItems[0]?.currency;
    }
    return '';
  }, [issueItems]);

  const columns = useTableIssuesColumns();

  const table = useTableIssues(issueItems, {
    columns,
    muiTableBodyRowProps: { hover: false },
  });

  if (!issueItems || issueItems?.length < 1) {
    return null;
  }

  return (
    <div className='mt-32 xl:mt-48'>
      <h3 className='mb-24 xl:mb-32 text-16 xl:text-20 font-bold flex items-center gap-8'>Invoice Issue Summary</h3>
      <DisputeTable
        table={table}
        footer={
          <TableRow>
            <TableCell align='left' colSpan={4}>
              <strong>Total Amount in Dispute</strong>
            </TableCell>
            <TableCell align='left'>
              <strong className='text-red-40'>{formatMoney(totalAmount, totalCurrency)}</strong>
            </TableCell>
          </TableRow>
        }
      />
    </div>
  );
};
